import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import useQuery from "../../../helpers/useQuery";
import { AdminService } from "../../../services";
import { TABS, mapOrganisation, tabKeys } from "./config";
import { defaultFilters } from "datastake-daf/src/helpers/Table";

const getFilters = (filters) => {
  const toReturn = {};

  if (filters.userType) {
    toReturn['users.role'] = filters.userType;
  }

  toReturn.application = filters.application;
  toReturn.category = filters.category;
  toReturn.country = filters.country;

  return toReturn;
}

export const useAdminUsers = ({
  setLoading = () => { },
  getUsers = () => { },
}) => {
  const query = useQuery();
  const push = useNavigate();
  const activeAbort = useRef();
  const blockedAbort = useRef();
  const requestsAbort = useRef();
  const [requestsFetched, setRequestsFetched] = useState(false);
  const [usersFetched, setUsersFetched] = useState(false);
  const [blockedFetched, setBlockedFetched] = useState(false);
  const [requests, setRequests] = useState([]);
  const [blocked, setBlocked] = useState([]);
  const { i18n: { language } } = useTranslation();
  const [activeTab, setActiveTab] = useState(query.get('activeTab') || tabKeys[0]);
  const [visibleTabs, setVisibleTabs] = useState(tabKeys.reduce((all, k) => {
    if (k === activeTab) {
      all[k] = true;
      return all;
    }

    all[k] = false;
    return all;
  }, {}));

  const [perPageObject, setPerPageObject] = useState(tabKeys.reduce((all, k) => {
    if (k === activeTab) {
      all[k] = Number.parseInt(query.get('perPage')) || defaultFilters.pageSize;
    } else {
      all[k] = defaultFilters.pageSize;
    }
    return all;
  }, {}));

  const [pageObject, setPageObject] = useState(tabKeys.reduce((all, k) => {
    all[k] = 1;
    return all;
  }, {}));

  const [searchObject, setSearchObject] = useState(tabKeys.reduce((all, k) => {
    if (k === activeTab) {
      all[k] = query.get('search') || '';
    } else {
      all[k] = '';
    }
    return all;
  }, {}));

  const [activeFiltersObject, setActiveFiltersObject] = useState(tabKeys.reduce((all, k) => {
    if (k === activeTab) {
      try {
        const parsed = JSON.parse(query.get('activeFilters'));
        all[k] = parsed || {};
      } catch (err) {
        all[k] = {};
      }
    } else {
      all[k] = {};
    }
    return all;
  }, {}));

  const [optionsObject, setOptionsObject] = useState(tabKeys.reduce((all, k) => {
    all[k] = {};
    return all;
  }, {}));

  const [totalsObject, setTotalsObject] = useState(tabKeys.reduce((all, k) => {
    all[k] = 0;
    return all;
  }, {}));

  const setPage = useCallback((key, val) =>
    setPageObject((p) => ({ ...p, [key]: val })), []);

  const setTotal = useCallback((key, val) =>
    setTotalsObject((p) => ({ ...p, [key]: val })), []);

  const setOptions = useCallback((key, val) =>
    setOptionsObject((p) => ({ ...p, [key]: val })), []);

  const perPage = useMemo(() => perPageObject[activeTab], [perPageObject, activeTab]);
  const setPerPage = useCallback((val) =>
    setPerPageObject((p) => ({ ...p, [activeTab]: val })), [activeTab]);

  const search = useMemo(() => searchObject[activeTab], [searchObject, activeTab]);
  const setSearch = useCallback((val) =>
    setSearchObject((p) => ({ ...p, [activeTab]: val })), [activeTab]);

  const activeFilters = useMemo(() => activeFiltersObject[activeTab], [activeFiltersObject, activeTab]);
  const setActiveFilters = useCallback((val) =>
    setActiveFiltersObject((p) => ({ ...p, [activeTab]: val })), [activeTab]);

  useEffect(() => {
    if (!visibleTabs[activeTab]) {
      setVisibleTabs((prev) => ({ ...prev, [activeTab]: true }));
    }
  }, [activeTab]);

  useEffect(() => {
    const parsedFilters = JSON.stringify(activeFilters);
    push(
      `${window.location.pathname}?activeTab=${activeTab}&perPage=${perPage}&search=${search}&activeFilters=${parsedFilters}`
    );
  }, [activeTab, perPage, search, activeFilters]);

  const fetchRequests = useCallback(async ({ page, perPage, search, activeFilters }) => {
    setLoading(true);
    const filters = getFilters(activeFilters || activeFiltersObject[TABS.ACTIVE]);

    if (requestsAbort.current) {
      requestsAbort.current.abort();
    }

    const abortC = new AbortController();
    requestsAbort.current = abortC;

    try {
      const { data } = await AdminService.requests({
        perPage: perPage || perPageObject[TABS.REQUESTS],
        page: page || pageObject[TABS.REQUESTS],
        filters,
        search: search || searchObject[TABS.REQUESTS],
      }, abortC.signal);

      setRequestsFetched(true);
      setRequests(data?.data?.map(mapOrganisation));
      setOptions(TABS.REQUESTS, data?.options);
      setTotal(TABS.REQUESTS, data?.meta?.total || 0);
    } catch (err) {
      console.log(err);
    }

    setLoading(false);
  }, [pageObject, perPageObject, activeFiltersObject, searchObject]);

  const fetchBlocked = useCallback(async ({ page, perPage, search, activeFilters }) => {
    setLoading(true);
    const filters = getFilters(activeFilters || activeFiltersObject[TABS.ACTIVE]);

    if (blockedAbort.current) {
      blockedAbort.current.abort();
    }

    const abortC = new AbortController();
    blockedAbort.current = abortC;

    try {
      const { data } = await AdminService.users({
        perPage: perPage || perPageObject[TABS.BLOCKED],
        page: page || pageObject[TABS.BLOCKED],
        status: 'inactive',
        filters,
        search: search || searchObject[TABS.BLOCKED],
      }, abortC.signal);

      setBlockedFetched(true);
      setBlocked(data.data.map(mapOrganisation));
      setOptions(TABS.BLOCKED, data.options);
      setTotal(TABS.BLOCKED, data?.meta?.total || 0);
    } catch (err) {
      console.log(err);
    }

    setLoading(false);
  }, [pageObject, perPageObject, activeFiltersObject, searchObject]);

  const fetchUsers = useCallback(({ page, perPage, activeFilters, search }) => {
    const filters = getFilters(activeFilters || activeFiltersObject[TABS.ACTIVE]);

    if (activeAbort.current) {
      activeAbort.current.abort();
    }

    const abortC = new AbortController();
    activeAbort.current = abortC;

    getUsers({
      perPage: perPage || perPageObject[TABS.ACTIVE],
      page: page || pageObject[TABS.ACTIVE],
      status: 'active',
      filters,
      search: search || searchObject[TABS.ACTIVE],
      signal: abortC.signal,
    }, () => {
      setUsersFetched(true);
    })
  }, [getUsers, setUsersFetched, perPageObject, pageObject, activeFiltersObject, searchObject]);

  useEffect(() => {
    if (!requestsFetched) {
      fetchRequests({});
    }
  }, [requestsFetched]);

  useEffect(() => {
    setRequestsFetched(false);
    setUsersFetched(false);
  }, [language]);

  return {
    options: optionsObject,
    setOptions,
    perPage,
    setPerPage,
    search,
    setSearch,
    activeFilters,
    setActiveFilters,
    setActiveTab,
    activeTab,
    perPageObject,
    searchObject,
    activeFiltersObject,
    visibleTabs,
    fetchRequests,
    requests,
    usersFetched,
    setUsersFetched,
    requestsFetched,
    totalsObject,
    setTotal,
    setPage,
    blocked,
    fetchBlocked,
    blockedFetched,
    fetchUsers,
  };
};
